import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Lightbulb, RefreshCw, Sparkles, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranscript } from "@/hooks/transcripts/useTranscripts";
import { TranscriptThreadHeader } from "./TranscriptThreadHeader";
import { TranscriptContent } from "./TranscriptContent";
import { TranscriptSummaryTab } from "./TranscriptSummaryTab";
import { TranscriptInsightsTab } from "./TranscriptInsightsTab";

const ThreadSkeleton: React.FC = () => (
	<div className="flex h-full flex-col" aria-hidden>
		<div className="flex shrink-0 items-start gap-3 border-b border-border px-4 py-3">
			<Skeleton className="h-9 w-9 shrink-0 rounded-sm" />
			<div className="flex-1 space-y-2">
				<Skeleton className="h-4 w-1/2" />
				<Skeleton className="h-3 w-3/4" />
			</div>
		</div>
		<div className="flex-1 space-y-4 p-4">
			{Array.from({ length: 6 }).map((_, index) => (
				<div key={index} className={index % 2 === 0 ? "flex gap-3" : "flex flex-row-reverse gap-3"}>
					<Skeleton className="h-8 w-8 shrink-0 rounded-sm" />
					<Skeleton className={index % 3 === 0 ? "h-10 w-3/5 rounded-sm" : "h-8 w-2/5 rounded-sm"} />
				</div>
			))}
		</div>
	</div>
);

export interface TranscriptThreadProps {
	transcriptId: string;
	onBack?: () => void;
	contextPanelOpen?: boolean;
	onToggleContextPanel?: () => void;
	onDeleted: () => void;
}

/**
 * The detail pane: meeting header plus Transcript / AI Summary / Insights
 * tabs. Fetches its own data so deep links work without the list.
 */
export const TranscriptThread: React.FC<TranscriptThreadProps> = ({
	transcriptId,
	onBack,
	contextPanelOpen,
	onToggleContextPanel,
	onDeleted,
}) => {
	const { t } = useTranslation();
	const { data: transcript, isLoading, isError, refetch, isPlaceholderData } = useTranscript(transcriptId);
	const [activeTab, setActiveTab] = useState("transcript");

	// Reset the tab when switching transcripts, not on data refreshes.
	useEffect(() => {
		setActiveTab("transcript");
	}, [transcriptId]);

	if (isLoading || (isPlaceholderData && !transcript?.transcript_content && transcript?.has_content))
		return <ThreadSkeleton />;

	if (isError || !transcript)
		return (
			<div className="flex h-full flex-col items-center justify-center p-6">
				<EmptyState
					icon={Video}
					title={t("transcripts.errors.detailTitle", "Couldn't load this transcript")}
					description={t("transcripts.errors.detailDescription", "It may have been deleted, or something went wrong.")}
				/>
				<Button variant="outline" size="sm" className="mt-2 gap-2" onClick={() => refetch()}>
					<RefreshCw className="h-4 w-4" />
					{t("transcripts.errors.retry", "Retry")}
				</Button>
			</div>
		);

	return (
		<div className="flex h-full min-h-0 flex-col bg-background">
			<TranscriptThreadHeader
				transcript={transcript}
				onBack={onBack}
				contextPanelOpen={contextPanelOpen}
				onToggleContextPanel={onToggleContextPanel}
				onDeleted={onDeleted}
			/>
			<Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
				<TabsList className="mx-4 mt-3 w-fit shrink-0">
					<TabsTrigger value="transcript" className="gap-1.5">
						<FileText className="h-3.5 w-3.5" aria-hidden />
						{t("transcripts.tabs.fullTranscript", "Transcript")}
					</TabsTrigger>
					<TabsTrigger value="summary" className="gap-1.5">
						<Sparkles className="h-3.5 w-3.5" aria-hidden />
						{t("transcripts.tabs.aiSummary", "AI Summary")}
					</TabsTrigger>
					<TabsTrigger value="insights" className="gap-1.5">
						<Lightbulb className="h-3.5 w-3.5" aria-hidden />
						{t("transcripts.tabs.insights", "Insights")}
					</TabsTrigger>
				</TabsList>
				<TabsContent
					value="transcript"
					className="mt-3 min-h-0 flex-1 data-[state=inactive]:hidden"
				>
					<TranscriptContent transcript={transcript} />
				</TabsContent>
				<TabsContent value="summary" className="mt-3 min-h-0 flex-1 data-[state=inactive]:hidden">
					<TranscriptSummaryTab transcript={transcript} />
				</TabsContent>
				<TabsContent value="insights" className="mt-3 min-h-0 flex-1 data-[state=inactive]:hidden">
					<TranscriptInsightsTab transcript={transcript} />
				</TabsContent>
			</Tabs>
		</div>
	);
};
